import React from 'react';
import styles from './HistoricalListingCard.module.css';

// Utility functions (moved from HistoricalListingsTable)
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return '';
  
  // Ultra-compact date format (MM/DD/YY)
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear().toString().substr(2, 2);
  
  return `${month}/${day}/${year}`;
};

// Helper to check if agent is John Snippen
const isJohnSnippen = (agentName) => {
  return agentName && agentName.includes('John') && agentName.includes('Snippen');
};

// Helper to abbreviate specific long agency names
const abbreviateLongName = (name) => {
  const abbreviations = {
    "Cascade Hasson Sotheby's International Realty": "Cascade Hasson SIR",
    "Harcourts The Garner Group Real Estate": "Harcourts Garner Group",
    "Knightsbridge International": "Knightsbridge Int'l"
    // Add more as needed
  };
  return abbreviations[name] || name;
};

// Helper to strip parentheses from office names
const stripParens = (str) => {
  return str ? str.replace(/\s*\([^)]*\)/g, '').trim() : str;
};

const HistoricalListingCard = ({ listing, isSplitMode, onHover, onClick, highlighted }) => {
  // Helper function for price formatting - reused from table component
  const formatPrice = (listing) => {
    const price = listing.closePrice || listing['Close Price'] || listing.price || listing.ListPrice;
    return price ? `$${parseInt(price, 10).toLocaleString()}` : '';
  };

  // Helper function for details text formatting - reused from table component
  const formatDetailsText = (listing) => {
    // Check for property type
    if (listing['Property Type'] === 'Land') {
      return listing['Book Section'] || '';
    }
    
    if (listing['Card Format'] === 'Residential Income') {
      return listing['Book Section'] || '';
    }
    
    // Get bedrooms and bathrooms values using all possible field names from the data
    const bedrooms = listing['Bedrooms'] || listing['Bedrooms Total'] || listing['# Beds'] || '0';
    
    // Handle different formats of bathroom values
    let bathrooms = listing['Bathrooms'] || listing['Bathrooms Total Integer'] || listing['Bathrooms Total'] || listing['# Baths'] || '0';
    if (bathrooms && typeof bathrooms === 'string') {
      bathrooms = parseFloat(bathrooms).toFixed(0);
    } else if (bathrooms) {
      bathrooms = Math.round(bathrooms).toString();
    }
    
    // Get square footage using all possible field names
    const sqft = listing['Total Living Area SqFt'] || listing['SqFt'] || listing['Sq Ft'] || '0';
    const formattedSqft = sqft ? Number(sqft).toLocaleString() : '0';
    
    // Build the display string with bullet points
    const parts = [];
    if (bedrooms && bedrooms !== '0') parts.push(`${bedrooms}bd`);
    if (bathrooms && bathrooms !== '0') parts.push(`${bathrooms}ba`);
    if (formattedSqft && formattedSqft !== '0') parts.push(`${formattedSqft} sqft`);
    
    return parts.join(' • ');
  };

  // Extract listing identifier - use List Number which appears to be consistent
  const listingId = listing['List Number'] || listing.id || listing['MLS #'] || listing['ML #'] || 
                   listing['MLS Number'] || listing['MLSNumber'] || 
                   (listing['Street Address'] && listing['City'] ? 
                     `${listing['Street Address']}-${listing['City']}` : 
                     `listing-${Math.random().toString(36).substring(2, 10)}`);
  
  console.log('[ListingCard] Extracted ID from listing:', listingId);

  // Handle hover events
  const handleMouseEnter = () => {
    console.log('[ListingCard] Mouse enter, sending ID:', listingId);
    if (onHover) onHover(listingId);
  };
  
  const handleMouseLeave = () => {
    console.log('[ListingCard] Mouse leave, sending null');
    if (onHover) onHover(null);
  };
  
  // Handle click event
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent click from bubbling up
    console.log('[ListingCard] Clicked, sending ID:', listingId);
    if (onClick) onClick(listingId);
  };

  return (
    <div 
      id={`listing-item-${listingId}`}
      className={`${styles.card} ${isSplitMode ? styles.splitMode : styles.galleryMode} ${highlighted ? styles.highlighted : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Photo Column */}
      <div className={styles.photoColumn}>
        {listing['Photo URL'] ? (
          <img 
            src={listing['Photo URL']} 
            alt={`${listing['Subdivision Name'] || listing['City'] || 'Property'}`}
            className={styles.propertyImage}
          />
        ) : (
          <div className={styles.placeholderImage}>No Photo</div>
        )}
      </div>

      {/* Core Info - Always Visible */}
      <div className={styles.coreInfo}>
        <div className={styles.neighborhood}>
          {(!listing['Subdivision Name'] || listing['Subdivision Name'].toUpperCase() === 'N/A') 
            ? (listing['City'] || '') 
            : listing['Subdivision Name']}
        </div>
        <div className={styles.city}>{listing['City']}</div>
        <div className={styles.date}>{formatDate(listing['Close Date'])}</div>
        <div className={styles.price}>{formatPrice(listing)}</div>
      </div>

      {/* Extended Info - Only Visible in Gallery Mode */}
      {!isSplitMode && (
        <div className={styles.extendedInfo}>
          <div className={styles.details}>{formatDetailsText(listing)}</div>
          <div className={styles.agent} style={{
            fontWeight: isJohnSnippen(listing['Listing Agent']) ? 600 : 400,
          }}>
            {isJohnSnippen(listing['Listing Agent']) 
              ? 'John Snippen' 
              : abbreviateLongName(stripParens(listing['Agency Name'] || 'U'))}
          </div>
          <div className={styles.office} style={{
            fontWeight: isJohnSnippen(listing['Selling Agent']) ? 600 : 400,
          }}>
            {isJohnSnippen(listing['Selling Agent'])
              ? 'John Snippen'
              : abbreviateLongName(stripParens(listing['Selling Agency'] || 'U'))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalListingCard;
